import { Button, Flex, Link, Text } from 'rebass';
import React, { useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import theme from '../../../styles/theme';
import { LogoSvg } from './Logo';
import Select from 'react-select';
import { isMobile } from 'react-device-detect';

const StyledLink = styled(Link)`
  color: #000;
`;

const slideLeft = keyframes`
  0% {
    opacity: 0;
    transform: translateY(15px);
  };
  100% {
    opacity: 1;
    transform: translateY(0px);
  };
`;

const FootNote = styled(Flex)`
  opacity: 0;
  animation-delay: 1.24s;
  animation-direction: normal;
  animation-duration: 0.4s;
  animation-fill-mode: forwards;
  animation-iteration-count: 1;
  animation-name: ${slideLeft};
  animation-play-state: running;
  animation-timing-function: cubic-bezier(0.64, 0, 0.28, 1);
  box-sizing: border-box;
  flex: 1;
  flex-direction: column;
  justify-content: flex-end;
`;

const Rect = styled.rect`
  transition: all var(0.3s) linear;
`;

const CheckboxWrap = styled(Flex)`
  border-color: rgba(0, 0, 0, 0.19);
  border-width: 2px;
  border-radius: 9px;
  border-style: solid;
  transition: all var(0.3s) linear;
  overflow: hidden;
`;

const customStyles = {
  control: (base, state) => ({
    ...base,
    border: 'none', // Removes the default border
    boxShadow: 'none', // Removes the default box shadow
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0, // Remove padding from the value container
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    display: 'none',
  }),
};

export const PickState = ({ title, state, id, onChange, onContinue, regions, setRegion }) => {
  const selectRef = useRef(null);

  const handleButtonClick = () => {
    if (selectRef.current) {
      selectRef.current.focus();
    }
  };

  return (
    <Flex flex={1} flexDirection={'column'}>
      <LogoSvg />
      <Text style={{ fontSize: isMobile ? '26px' : '32px', fontWeight: 500 }} textAlign={isMobile?'center': ''} mb={4}>
        First, let’s make sure we have licensed providers in your state:
      </Text>

      <Flex
        flexDirection={'row'}
        onClick={handleButtonClick}
        style={{ border: `1px solid #909ebb`, borderRadius: '12px', padding: '12px 16px', background: 'white', cursor: 'pointer', alignItems: 'center' }}
      >
        <Flex flex={1} flexDirection={'column'}>
          <Text mb={0} style={{ opacity: 0.7 }} fontSize={'13px'} fontWeight={500}>
            State
          </Text>

          <Select
            ref={selectRef}
            options={regions}
            value={regions.filter((element) => element.value === state['prestate'])}
            onChange={(option) => {
              onChange('prestate', option.value);
              setRegion(option);
            }}
            openMenuOnFocus={true}
            styles={customStyles}
          />
        </Flex>

        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5.83984 9.59001L11.4998 15.25L17.1598 9.59001L16.4498 8.89001L11.4998 13.84L6.54984 8.89001L5.83984 9.59001Z" fill="#7992B3" />
        </svg>
      </Flex>

      <Flex mt={4} style={{ alignItems: 'flex-start' }} onClick={() => onChange('acceptTerms', !state['acceptTerms'])}>
        <CheckboxWrap style={{ borderColor: state['acceptTerms'] ? theme.color.pink : '' }}>
          <Flex flex={1} style={{ background: 'white', borderRadius: '3px', border: '2px solid #fdf5f2', overflow: 'hidden' }}>
            <svg width="17" height="17" viewBox="0 0 17 17" xmlns="http://www.w3.org/2000/svg" fill={state['acceptTerms'] ? theme.color.pink : '#fdf5f2'}>
              <Rect width="17" height="17" rx="5" />
              {!!state['acceptTerms'] && <path d="M5 8.72727L7.1875 11L12 6" stroke="rgba(255,255,255, 1)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />}
            </svg>
          </Flex>
        </CheckboxWrap>
        <Text ml={3} flex={1} fontSize={isMobile ? '14px' : ''}>
          I agree to Paloma Health's&nbsp;
          <StyledLink href="https://www.palomahealth.com/legal-doc/paloma-health-terms-of-use" target="_blank">
            Terms of Service
          </StyledLink>{' '}
          and{' '}
          <StyledLink href="https://www.palomahealth.com/legal-doc/consent-to-telehealth" target="_blank">
            Informed Consent
          </StyledLink>
        </Text>
      </Flex>

      <Flex mt={3} style={{ alignItems: 'flex-start' }} onClick={() => onChange('hipaaNotice', !state['hipaaNotice'])}>
        <CheckboxWrap style={{ borderColor: state['hipaaNotice'] ? theme.color.pink : '' }}>
          <Flex flex={1} style={{ background: 'white', borderRadius: '3px', border: '2px solid #fdf5f2', overflow: 'hidden' }}>
            <svg width="17" height="17" viewBox="0 0 17 17" xmlns="http://www.w3.org/2000/svg" fill={state['hipaaNotice'] ? theme.color.pink : '#fdf5f2'}>
              <Rect width="17" height="17" rx="5" />
              {!!state['hipaaNotice'] && <path d="M5 8.72727L7.1875 11L12 6" stroke="rgba(255,255,255, 1)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />}
            </svg>
          </Flex>
        </CheckboxWrap>
        <Text ml={3} flex={1} fontSize={isMobile ? '14px' : ''}>
          I have read and agree to the terms of Paloma Health's&nbsp;
          <StyledLink href="https://www.palomahealth.com/legal-doc/notice-of-privacy-practices" target="_blank">
            HIPAA Notice
          </StyledLink>
        </Text>
      </Flex>

      <FootNote mb={4}>
        <Button variant={'primary'} onClick={onContinue} disabled={!(state || {})['prestate'] || !(state || {})['acceptTerms'] || !(state || {})['hipaaNotice']}>
          Continue
        </Button>
      </FootNote>
    </Flex>
  );
};
